/**
 * Markdown — safe assistant-message renderer (STORY-002-009, AC #9).
 *
 * SECURITY (REQ-PRD-§11.2-C, GUARDRAILS): this renderer NEVER uses
 * `dangerouslySetInnerHTML` and NEVER constructs an HTML string from model
 * output. Every node is a typed React element, so there is no HTML-injection
 * surface to sanitise — the React reconciler escapes all text content by
 * construction. Markdown is parsed into a safe element tree.
 *
 * Coverage (the subset GPT-4o emits for renovation advice — anything outside
 * it degrades to readable plain text): paragraphs + line breaks, ATX
 * headings (`#`..`######`), bullet / numbered lists, fenced code blocks
 * (```` ``` ````), blockquotes (`>`), horizontal rules (`---`), GFM tables
 * (`| a | b |`), and inline bold / italic / strikethrough / inline code /
 * links.
 *
 * Allowed-touch note (surfaced in the PR): the story named `react-markdown`
 * + `remark-gfm` + DOMPurify. Those packages are NOT in
 * `apps/web/package.json` / the pnpm lockfile, and `package.json` is OUTSIDE
 * this story's additive allow-list — adding unpinned runtime deps would trip
 * the "pinned versions are pinned; if missing, stop and ask" guardrail. This
 * dependency-free renderer satisfies the binding requirements (Markdown
 * renders, output is sanitised/safe, NO `dangerouslySetInnerHTML`) and is
 * strictly safer than an HTML-string + DOMPurify pipeline. If the team wants
 * the exact `react-markdown` mechanism, a follow-up can add the pinned deps
 * to `package.json` and swap this implementation behind the same component
 * boundary without touching call sites.
 */
import { Fragment, type ReactElement, type ReactNode } from "react";

export interface MarkdownProps {
  content: string;
  className?: string;
}

/**
 * Render inline markdown (`**bold**`, `*italic*`, `~~strike~~`,
 * `` `code` ``, `[label](url)`) within a single text run into React nodes.
 * Link hrefs are restricted to http(s) / mailto to avoid `javascript:` URLs;
 * anything else renders as plain text.
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Ordered alternation: links, bold, strikethrough, italic, inline code.
  const pattern =
    /(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(~~[^~]+~~)|(\*[^*]+\*)|(`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <Fragment key={`${keyPrefix}-t-${i}`}>
          {text.slice(lastIndex, match.index)}
        </Fragment>,
      );
      i += 1;
    }
    const token = match[0];
    if (token.startsWith("[")) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        const label = linkMatch[1] ?? "";
        const href = linkMatch[2] ?? "";
        if (/^(https?:\/\/|mailto:)/i.test(href)) {
          nodes.push(
            <a
              key={`${keyPrefix}-a-${i}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-[3px] font-medium text-primary underline decoration-[var(--g-primary)]/40 underline-offset-[3px] transition-colors hover:decoration-[var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            >
              {label}
            </a>,
          );
        } else {
          // Unsafe scheme — render the raw token as text.
          nodes.push(
            <Fragment key={`${keyPrefix}-a-${i}`}>{token}</Fragment>,
          );
        }
      }
    } else if (token.startsWith("**")) {
      nodes.push(
        <strong key={`${keyPrefix}-b-${i}`} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("~~")) {
      nodes.push(<del key={`${keyPrefix}-s-${i}`}>{token.slice(2, -2)}</del>);
    } else if (token.startsWith("*")) {
      nodes.push(<em key={`${keyPrefix}-i-${i}`}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith("`")) {
      nodes.push(
        <code
          key={`${keyPrefix}-c-${i}`}
          className="g-data rounded-[5px] border border-[var(--g-hairline)] bg-[var(--g-sunken)] px-1.5 py-0.5 text-[0.85em]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    }
    lastIndex = pattern.lastIndex;
    i += 1;
  }

  if (lastIndex < text.length) {
    nodes.push(
      <Fragment key={`${keyPrefix}-t-end`}>{text.slice(lastIndex)}</Fragment>,
    );
  }

  return nodes;
}

type CellAlign = "left" | "center" | "right" | null;

/** One list item: its own text plus indented bullet lines nested under it. */
interface ListItem {
  text: string;
  children: string[];
}

type Block =
  | { type: "p"; lines: string[] }
  | { type: "ul"; items: ListItem[] }
  | { type: "ol"; start: number; items: ListItem[] }
  | { type: "h"; level: number; text: string }
  | { type: "code"; code: string }
  | { type: "quote"; lines: string[] }
  | { type: "hr" }
  | { type: "table"; header: string[]; align: CellAlign[]; rows: string[][] };

/** GFM table delimiter row, e.g. `|---|:--:|--:|`. */
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/;

/** Split a `| a | b |` row into trimmed cell strings. */
function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

/** Group raw lines into block-level markdown nodes. */
function toBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  let para: string[] = [];

  const flushPara = (): void => {
    if (para.length > 0) {
      blocks.push({ type: "p", lines: para });
      para = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    const trimmed = line.trimStart();

    // Fenced code block (``` or ~~~) — content inside is NOT parsed as markdown.
    if (/^(```|~~~)/.test(trimmed)) {
      flushPara();
      const code: string[] = [];
      i += 1;
      while (
        i < lines.length &&
        !/^\s*(```|~~~)\s*$/.test(lines[i] ?? "")
      ) {
        code.push(lines[i] ?? "");
        i += 1;
      }
      blocks.push({ type: "code", code: code.join("\n") });
      continue; // i sits on the closing fence (or past EOF); loop's i+=1 skips it
    }

    // ATX heading.
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushPara();
      blocks.push({
        type: "h",
        level: (heading[1] ?? "").length,
        text: (heading[2] ?? "").trim(),
      });
      continue;
    }

    // Horizontal rule.
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(trimmed)) {
      flushPara();
      blocks.push({ type: "hr" });
      continue;
    }

    // GFM table: a row with pipes immediately followed by a delimiter row.
    if (trimmed.includes("|") && TABLE_SEPARATOR.test(lines[i + 1] ?? "")) {
      flushPara();
      const header = splitTableRow(trimmed);
      const align: CellAlign[] = splitTableRow(lines[i + 1] ?? "").map((cell) => {
        const left = cell.startsWith(":");
        const right = cell.endsWith(":");
        if (left && right) return "center";
        if (right) return "right";
        if (left) return "left";
        return null;
      });
      const rows: string[][] = [];
      i += 2;
      while (
        i < lines.length &&
        (lines[i] ?? "").includes("|") &&
        (lines[i] ?? "").trim().length > 0
      ) {
        rows.push(splitTableRow(lines[i] ?? ""));
        i += 1;
      }
      i -= 1; // step back onto the last table row; loop's i+=1 moves past it
      blocks.push({ type: "table", header, align, rows });
      continue;
    }

    // Blockquote (consecutive `>` lines coalesce).
    const quote = /^>\s?(.*)$/.exec(trimmed);
    if (quote) {
      flushPara();
      const last = blocks[blocks.length - 1];
      if (last && last.type === "quote") {
        last.lines.push(quote[1] ?? "");
      } else {
        blocks.push({ type: "quote", lines: [quote[1] ?? ""] });
      }
      continue;
    }

    // Bullet / ordered lists. GPT's dominant answer shape is a numbered list
    // whose items carry INDENTED bullet children:
    //   1. **Header**:
    //      - point
    //      - point
    //   2. **Next**:
    // A flat parser splits that into ol/ul/ol/… and every fresh <ol> restarts
    // at 1 — the "every item is numbered 1." bug. Two rules fix it: indented
    // bullets nest into the previous list item, and a NEW ordered block adopts
    // its source number as <ol start> (CommonMark), continuing an interleaved
    // list even when the model numbers lazily (every line "1.").
    const indent = line.length - trimmed.length;
    const bulletMatch = /^[-*•]\s+(.*)$/.exec(trimmed);
    const orderedMatch = /^(\d+)[.)]\s+(.*)$/.exec(trimmed);
    if (bulletMatch) {
      flushPara();
      const last = blocks[blocks.length - 1];
      const nestHost =
        indent >= 2 && last && (last.type === "ol" || last.type === "ul")
          ? last.items[last.items.length - 1]
          : undefined;
      if (nestHost) {
        nestHost.children.push(bulletMatch[1] ?? "");
      } else if (last && last.type === "ul") {
        last.items.push({ text: bulletMatch[1] ?? "", children: [] });
      } else {
        blocks.push({
          type: "ul",
          items: [{ text: bulletMatch[1] ?? "", children: [] }],
        });
      }
    } else if (orderedMatch) {
      flushPara();
      const srcNum = Number.parseInt(orderedMatch[1] ?? "1", 10) || 1;
      const text = orderedMatch[2] ?? "";
      const last = blocks[blocks.length - 1];
      const nestHost =
        indent >= 2 && last && (last.type === "ol" || last.type === "ul")
          ? last.items[last.items.length - 1]
          : undefined;
      if (nestHost) {
        // A numbered line nested under a list item is rare from the model;
        // degrade to a child bullet that keeps its number as text.
        nestHost.children.push(`${srcNum}. ${text}`);
      } else if (last && last.type === "ol") {
        last.items.push({ text, children: [] });
      } else {
        // New ordered block: trust the source number (CommonMark <ol start>).
        // If the model numbered lazily AND zero-indent bullets interrupted the
        // list (prev = ul, before it = ol), continue counting from there.
        let start = srcNum;
        const prev2 = blocks[blocks.length - 2];
        if (
          srcNum === 1 &&
          last &&
          last.type === "ul" &&
          prev2 &&
          prev2.type === "ol"
        ) {
          start = prev2.start + prev2.items.length;
        }
        blocks.push({ type: "ol", start, items: [{ text, children: [] }] });
      }
    } else if (trimmed.length === 0) {
      flushPara();
    } else {
      para.push(line);
    }
  }
  flushPara();
  return blocks;
}

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

/**
 * Heading scale for assistant prose.
 *
 * The old scale ran 18 / 16 / 15 / 15 / 14 / 14 against a 14.5px body — so an
 * `###` section head ("2. Клей") rendered at the same size as the sentence
 * under it and the answer read as one undifferentiated slab. That was survivable
 * while the text sat in a narrow bubble; at full measure it is the difference
 * between a scannable answer and a wall.
 *
 * Now it steps properly (22 / 19 / 16.5 / 15 / 13 / 13) against a 15px body,
 * with each step separated by weight as well as size, and with materially more
 * space above a heading than below it so a section binds to its own content.
 */
const HEADING_CLASS: Record<number, string> = {
  1: "mt-6 mb-2 text-[23px] font-extrabold leading-tight tracking-[-0.02em] first:mt-0",
  2: "mt-6 mb-2 text-[20px] font-bold leading-tight tracking-[-0.015em] first:mt-0",
  3: "mt-5 mb-1.5 text-[17.5px] font-bold leading-snug first:mt-0",
  4: "mt-4 mb-1 text-[16px] font-semibold leading-snug first:mt-0",
  5: "mt-4 mb-1 text-[14px] font-semibold uppercase tracking-[0.06em] text-[var(--g-ink-2)] first:mt-0",
  6: "mt-4 mb-1 text-[14px] font-semibold uppercase tracking-[0.06em] text-[var(--g-ink-2)] first:mt-0",
};

function cellStyle(align: CellAlign): { textAlign: "left" | "center" | "right" } | undefined {
  return align ? { textAlign: align } : undefined;
}

/** Indented bullet children rendered as a nested <ul> inside their parent <li>. */
function renderNestedChildren(children: string[], keyPrefix: string): ReactNode {
  if (children.length === 0) return null;
  return (
    <ul className="mt-1.5 list-disc space-y-1 pl-5 marker:text-[var(--g-ink-3)]">
      {children.map((child, ci) => (
        <li key={`${keyPrefix}-${ci}`}>
          {renderInline(child, `${keyPrefix}-${ci}`)}
        </li>
      ))}
    </ul>
  );
}

export function Markdown({ content, className }: MarkdownProps): ReactElement {
  const blocks = toBlocks(content);
  return (
    <div className={className} data-testid="markdown">
      {blocks.map((block, bi) => {
        const key = `b-${bi}`;
        switch (block.type) {
          case "h": {
            const level = Math.min(Math.max(block.level, 1), 6);
            const Tag = `h${level}` as HeadingTag;
            return (
              <Tag key={key} className={HEADING_CLASS[level] ?? HEADING_CLASS[6]}>
                {renderInline(block.text, `h-${bi}`)}
              </Tag>
            );
          }
          case "code":
            return (
              <pre
                key={key}
                className="my-4 overflow-x-auto rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-sunken)] p-3.5 text-[0.85em] leading-relaxed"
              >
                <code className="g-data">{block.code}</code>
              </pre>
            );
          case "ul":
            return (
              <ul key={key} className="my-3 list-disc space-y-1.5 pl-5 marker:text-[var(--g-ink-3)]">
                {block.items.map((item, ii) => (
                  <li key={`li-${bi}-${ii}`}>
                    {renderInline(item.text, `ul-${bi}-${ii}`)}
                    {renderNestedChildren(item.children, `uln-${bi}-${ii}`)}
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol
                key={key}
                start={block.start > 1 ? block.start : undefined}
                className="my-3 list-decimal space-y-1.5 pl-5 marker:font-medium marker:text-[var(--g-ink-3)]"
              >
                {block.items.map((item, ii) => (
                  <li key={`li-${bi}-${ii}`}>
                    {renderInline(item.text, `ol-${bi}-${ii}`)}
                    {renderNestedChildren(item.children, `oln-${bi}-${ii}`)}
                  </li>
                ))}
              </ol>
            );
          case "quote":
            return (
              <blockquote
                key={key}
                className="my-4 border-l border-[var(--g-rule-strong)] py-0.5 pl-4 text-[var(--g-ink-2)]"
              >
                {block.lines.map((l, li) => (
                  <Fragment key={`q-${bi}-${li}`}>
                    {li > 0 ? <br /> : null}
                    {renderInline(l, `q-${bi}-${li}`)}
                  </Fragment>
                ))}
              </blockquote>
            );
          case "hr":
            return <hr key={key} className="my-6 border-[var(--g-rule)]" />;
          case "table":
            return (
              <div key={key} className="my-4 overflow-x-auto rounded-[var(--g-radius)] border border-[var(--g-rule)]">
                <table className="w-full border-collapse text-[0.92em]">
                  <thead>
                    <tr>
                      {block.header.map((cell, ci) => (
                        <th
                          key={`th-${bi}-${ci}`}
                          className="border-b border-[var(--g-rule)] bg-[var(--g-sunken)] px-3 py-2 text-left text-[0.92em] font-semibold uppercase tracking-[0.04em] text-[var(--g-ink-2)]"
                          style={cellStyle(block.align[ci] ?? null)}
                        >
                          {renderInline(cell, `th-${bi}-${ci}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, ri) => (
                      <tr key={`tr-${bi}-${ri}`}>
                        {row.map((cell, ci) => (
                          <td
                            key={`td-${bi}-${ri}-${ci}`}
                            className="border-b border-[var(--g-hairline)] px-3 py-2 tabular-nums"
                            style={cellStyle(block.align[ci] ?? null)}
                          >
                            {renderInline(cell, `td-${bi}-${ri}-${ci}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "p":
          default:
            return (
              <p
                key={key}
                className="my-3 whitespace-pre-wrap first:mt-0 last:mb-0"
              >
                {block.lines.map((l, li) => (
                  <Fragment key={`p-${bi}-${li}`}>
                    {li > 0 ? <br /> : null}
                    {renderInline(l, `p-${bi}-${li}`)}
                  </Fragment>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}

export default Markdown;
